import { useState } from 'react';
import { Button, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Tooltip, useMediaQuery } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useDownloadFileStore } from '@lib/store';
import type { FileEntity } from '@shared/api';
import { IconButton } from '@shared/widgets';

interface FileListProps {
  data: FileEntity[];
  showStatus?: boolean;
  deleteLoading?: boolean;
  onDeleteFile?: (fileId: string) => void;
  onRejectFile?: (fileId: string, fileName?: string) => void;
}

export const FileList = (props: FileListProps) => {
  const downloadFileStore = useDownloadFileStore();

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const largeScreen = useMediaQuery('(min-width:800px)');

  const canRemove = !!props.onDeleteFile;
  const hasStatus = !!props.onRejectFile || props.showStatus;
  const canReject = !!props.onRejectFile;
  return (
    <>
      {largeScreen ? (
        <Table aria-label='simple table' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 100 }} align='left'>
                File
              </TableCell>
              <TableCell style={{ width: 100 }} align='left'>
                Type
              </TableCell>
              <TableCell style={{ width: 100 }} align='center'>
                Date Uploaded
              </TableCell>
              {hasStatus && (
                <TableCell style={{ width: 100 }} align='center'>
                  Status
                </TableCell>
              )}
              <TableCell style={{ width: 100 }} align='center'>
                Download
              </TableCell>

              {canRemove && (
                <TableCell style={{ width: 100 }} align='center'>
                  Remove
                </TableCell>
              )}
              {canReject && (
                <TableCell style={{ width: 100 }} align='center'>
                  Reject
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.data.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Button
                    color='primary'
                    variant='text'
                    disabled={downloadFileStore.loading}
                    onClick={() => downloadFileStore.downloadFile({ fileId: row.id, filename: row.fileName })}
                  >
                    {row.fileName}
                  </Button>
                </TableCell>
                <TableCell>{row.type}</TableCell>
                <TableCell align='center'>{new Date(row.dateUploaded).toDateString()}</TableCell>
                {hasStatus && (
                  <TableCell align='center'>
                    <Tooltip title={row.rejectReason || ''}>
                      <span>{row.status}</span>
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell align='center'>
                  <IconButton
                    size='large'
                    loading={downloadFileStore.loading}
                    onClick={() => downloadFileStore.downloadFile({ fileId: row.id, filename: row.fileName })}
                  >
                    <GetAppIcon />
                  </IconButton>
                </TableCell>
                {canRemove && (
                  <TableCell align='center'>
                    <IconButton size='large' loading={props.deleteLoading} onClick={() => props.onDeleteFile?.(row.id)}>
                      <HighlightOffIcon />
                    </IconButton>
                  </TableCell>
                )}
                {canReject && (
                  <TableCell align='center'>
                    {row.status !== 'Rejected' ? (
                      <IconButton size='large' onClick={() => props.onRejectFile?.(row.id, row.fileName)}>
                        <HighlightOffIcon />
                      </IconButton>
                    ) : null}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!props.data.length && (
              <TableRow>
                <TableCell colSpan={4} align='center'>
                  No Records Found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      ) : (
        <Table aria-label='simple table' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 100 }} align='center'>
                File Id
              </TableCell>
              <TableCell style={{ width: 100 }} align='center'>
                Application Id
              </TableCell>
              <TableCell style={{ width: 100 }} align='center'>
                File Type
              </TableCell>
              <TableCell style={{ width: 100 }} align='center'>
                Date Uploaded
              </TableCell>
              <TableCell style={{ width: 100 }} align='center'>
                <GetAppIcon />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.data.map((row) => (
              <TableRow key={row.id}>
                <TableCell align='center'>{row.id}</TableCell>
                <TableCell align='center'>{row.applicationId}</TableCell>
                <TableCell align='center'>{row.type}</TableCell>
                <TableCell align='center'>{new Date(row.dateUploaded).toDateString()}</TableCell>
                <TableCell align='center'>Download</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component='div'
        count={props.data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, value) => setPage(value)}
        onRowsPerPageChange={(e) => {
          setPage(0);
          setRowsPerPage(+e.target.value);
        }}
      />
    </>
  );
};
